import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "reactstrap";
import Topbar from "./Topbar";
import Home from "./Home";
import MyTeam from "./Team";
import { useAuthentication } from "../Authentication/context";
import {
	getFirstRequestRoute,
	resolveRequestType,
	resolveTimesheetFilter,
} from "./permissions";

function Request({ namespace, timeoff = false }) {
	const navigate = useNavigate();
	const { accessMap } = useAuthentication();
	const [hasChanges, setHasChanges] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();
	const query = new URLSearchParams(searchParams);
	const requestType = query.get("requestType");

	const resolvedRequestType = useMemo(
		() => resolveRequestType(accessMap, namespace, requestType),
		[accessMap, namespace, requestType],
	);

	const [filterType, setFilterType] = useState(() => {
		const typeParam = query.get("type") || "all";
		if (namespace === "timesheet" && resolvedRequestType) {
			return resolveTimesheetFilter(accessMap, resolvedRequestType, typeParam) || typeParam;
		}
		return typeParam;
	});

	useEffect(() => {
		if (!accessMap) {
			return;
		}

		if (!resolvedRequestType) {
			const fallback = getFirstRequestRoute(accessMap);
			navigate(fallback);
			return;
		}

		if (requestType !== resolvedRequestType) {
			setSearchParams((prev) => {
				prev.set("requestType", resolvedRequestType);
				return prev;
			});
		}
	}, [resolvedRequestType, requestType, setSearchParams, navigate, accessMap]);

	useEffect(() => {
		if (!accessMap || namespace !== "timesheet" || !resolvedRequestType) {
			return;
		}
		const nextFilter = resolveTimesheetFilter(accessMap, resolvedRequestType, filterType || "all");
		if (nextFilter && nextFilter !== filterType) {
			setFilterType(nextFilter);
		}
	}, [accessMap, namespace, resolvedRequestType, filterType]);

	useEffect(() => {
		if (!accessMap || namespace !== "timesheet" || !resolvedRequestType || !filterType) {
			return;
		}
		setSearchParams((prev) => {
			prev.set("requestType", resolvedRequestType);
			prev.set("type", filterType);
			return prev;
		});
	}, [accessMap, namespace, resolvedRequestType, filterType, setSearchParams]);

    const content = useMemo(() => {
        if (resolvedRequestType === "my") {
            return (
                <Home
                    timeoff={timeoff}
                    namespace={namespace}
                    filterType={filterType}
                    hasChanges={hasChanges}
                    setHasChanges={setHasChanges}
                />
            );
        } else {
            return (
                <MyTeam
                    namespace={namespace}
                    timeoff={timeoff}
                    filterType={filterType}
                    hasChanges={hasChanges}
                    setHasChanges={setHasChanges}
                />
            )
        }
    }, [resolvedRequestType, namespace, filterType, timeoff, hasChanges, setHasChanges]);

	if (!resolvedRequestType) {
		return null;
	}

    return (
        <div className="d-flex h-100 flex-column">
            <Card className="flex-fill mb-0">
                <Topbar
                    namespace={namespace}
                    filterType={filterType}
                    setFilterType={setFilterType}
                    setSearchParams={setSearchParams}
                    setHasChanges={setHasChanges}
					requestType={resolvedRequestType}
					accessMap={accessMap}
                />
            </Card>

            <div style={{ height: "calc(100% - 70px)" }}>
                {content}
            </div>
        </div>
    );
}

export default Request;
